<template>
  <Modal
    :value="value"
    @hide="hide()"
    :skipCloseOnBgClick="false"
    title="Add/create package"
  >
    <template slot="body" class="content">
      <div
        v-if="source.permission && source.permission < 8"
        class="notification is-danger"
      >
        This action requires <i>Create</i> permission that you currently lack.
      </div>

      <p v-if="!file">
        Upload an .exe or .msi file to generate a deployable NuGet package, or
        just upload a NuGet package to your repository.
      </p>

      <div class="columns is-multiline">
        <div class="column is-12">
          <FileUploader
            :multiple="false"
            :hideSave="true"
            ref="fileUploader"
            btnName="Choose file"
            css="is-primary"
            :path="`${source.url}package/generate`"
            @ready="uploadReady($event)"
            @error="uploadError($event)"
            @upload="uploading = $event"
            @progress="progress = $event"
            @completed="uploadCompleted($event)"
          ></FileUploader>

          <div
            v-if="uploading && progress && progress !== 100"
            class="column is-12"
          >
            <div class="margin-top-1">
              <p>Uploading...</p>
              <progress class="progress is-info" :value="progress" max="100"
                >{{ progress }}%</progress
              >
            </div>
          </div>
          <div v-show="uploading && progress === 100" class="column is-12">
            <div class="margin-top-1">
              <p>Processing... This can take a while.</p>
              <progress class="progress is-primary" max="100">50%</progress>
            </div>
          </div>
        </div>

        <div
          v-if="file && file.ext !== 'nupkg' && !uploading && !message"
          class="column is-12"
        >
          <p class="no-bottom-margin">
            The NuGet generator engine will try to parse the <b>title</b>,
            <b>version</b>, and <b>author</b> from the file, but the safest bet
            is to type it yourself. The engine will warn if a parameter is
            missing.
          </p>
        </div>

        <div
          v-if="file && file.ext !== 'nupkg' && !uploading"
          class="column is-12"
        >
          <CheckboxText
            css="is-inline-block margin-right-10"
            :checked="form.upload"
            @click="form.upload = true"
            >Create and add to repo</CheckboxText
          >
          <CheckboxText
            css="is-inline-block "
            :checked="!form.upload"
            @click="form.upload = false"
            >Create and download</CheckboxText
          >
        </div>

        <div v-if="message" class="column is-12">
          <div class="notification is-warning">{{ message }}</div>
        </div>

        <div
          v-if="file && file.ext !== 'nupkg' && !uploading"
          class="column is-12"
        >
          <BForm :elements="form" @valid="form.valid = $event"></BForm>
        </div>
      </div>
    </template>
    <template slot="footer" class="content">
      <div v-if="!uploading">
        <div
          @click="upload(source)"
          :disabled="!valid"
          class="button is-success"
        >
          Upload
        </div>
        <div class="button" @click="hide()">Cancel</div>
      </div>
      <div v-show="uploading" class="spinner-wrapper center">
        <span class="spinner is-centered"></span>
      </div>
    </template>
  </Modal>
</template>

<style lang="css" scoped>
.no-bottom-margin {
  margin-bottom: 0px;
}
</style>

<script>
import { post, postApi } from "@/utils/http";
import { downloadFile } from "@/utils/document";

export default {
  props: { value: Boolean, source: Object, item: Object },
  components: {
    Modal: () => import("@/components/main/Modal.vue"),
    FileUploader: () => import("@/components/main/FileUploader.vue"),
    BForm: () => import("@/components/singles/BForm/component.vue"),
    CheckboxText: () => import("@/components/singles/CheckboxText.vue"),
  },
  data() {
    return {
      isLoading: false,
      file: null,
      message: null,

      uploading: false,
      progress: null,
      form: {
        upload: true,
        inputs: [
          {
            name: "id",
            placeholder: "Id",
            type: "text",
            label: null,
            validation: "required|alpha_dash|min:3",
            value: "",
            focus: true,
          },
          {
            name: "title",
            placeholder: "Title",
            type: "text",
            label: null,
            validation: "",
            value: "",
          },
          {
            name: "version",
            placeholder: "Version",
            type: "text",
            label: null,
            validation: {
              regex: /^(\d+(\s*\.\s*\d+){2,3})([-][a-z][0-9a-z-]*)?$/,
            },
            value: "",
          },
          {
            name: "installArgs",
            placeholder: "Install arguments (/S if empty)",
            type: "text",
            label: null,
            validation: "",
            value: "",
          },
          {
            name: "description",
            placeholder: "Description",
            type: "text",
            label: null,
            validation: "",
            value: "",
          },
          {
            name: "authors",
            placeholder: "Authors",
            type: "text",
            label: null,
            validation: "",
            value: "",
          },
          {
            name: "iconUrl",
            placeholder: "Icon url",
            type: "text",
            label: null,
            validation: {
              regex: /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/,
            },
            value: "",
          },
        ],
        valid: false,
      },
    };
  },
  created() {
    if (this.item) {
      this.form.inputs[0].value = this.item.chocoPackage.Id || "";
      this.form.inputs[1].value = this.item.chocoPackage.Title || "";
      this.form.inputs[3].value =
        this.item.chocoPackage.InstallationArguments || "";
      this.form.inputs[4].value = this.item.chocoPackage.Description || "";
      this.form.inputs[5].value = this.item.chocoPackage.Authors || "";
      this.form.inputs[6].value = this.item.chocoPackage.IconUrl || "";
      this.form.valid = true;
      this.validate();
    }
  },
  computed: {
    valid() {
      return this.validate();
    },
  },
  methods: {
    validate() {
      return (
        this.source &&
        this.file &&
        (this.form.valid || (this.file && this.file.ext === "nupkg"))
      );
    },
    uploadCompleted(result) {
      if (result && result.data && result.data.url) {
        this.$nextTick(() => {
          this.$helpers.notify(
            "Downloading",
            `Downloading ${this.file.fullName}.`,
            "success",
            -1
          );
          downloadFile(result.data.url);

          this.reset();
          this.hide();
        });
      } else {
        this.$helpers.notify(
          "Completed",
          `<i>${this.file.fullName}</i> is now added to <i>${this.source.name}</i>.`,
          "success",
          -1
        );

        this.$emit("change");
        this.reset();
        this.hide();
      }
    },
    reset() {
      if (this.$refs.fileUploader) this.$refs.fileUploader.reset();
      this.form.inputs.map((x) => {
        x.value = "";
        x.validation = "";
      });
      this.form.valid = true;
      this.isLoading = false;
      this.file = null;
      this.message = null;
      this.uploading = false;
      this.progress = null;
    },
    getInputValues() {
      const data = {};
      this.form.inputs.map((x) => (data[x.name] = x.value));
      data.source = this.source.url;
      data.upload = this.form.upload;
      return data;
    },
    uploadReady(file) {
      this.message = null;
      this.file = file;
      if (!file) this.reset();
    },

    uploadError(data) {
      if (data && data.response && data.response.status === 400) {
        this.message = data.response.data;
        this.form.valid = false;
        const arr = data.response.data.split(":");
        if (arr.length !== 0 && arr[1]) {
          const props = arr[1].split(",").map((x) => x.trim().toLowerCase());
          this.form.inputs.map((x) => {
            if (props.some((y) => y === x.name.toLowerCase()))
              x.validation = "required";
          });
        }
      } else if (data && data.response && data.response.status === 401) {
        this.$helpers.notify("", "Permission denied.", "danger");
        this.reset();
      } else if (data && data.response && data.response.status === 500) {
        this.$helpers.notify("Error", "Internal server error.", "danger", -1);
        this.reset();
      } else {
        this.$helpers.notify("Error", "Unknown client error.", "danger", -1);
        this.reset();
      }
    },
    async upload() {
      if (
        this.source &&
        this.file &&
        (this.form.valid || (this.file && this.file.ext === "nupkg"))
      ) {
        this.message = null;
        const data = this.getInputValues();
        await this.$refs.fileUploader.save(data);
      }
    },
    hide() {
      if (!this.uploading) {
        this.$emit("hide", false);
        this.$emit("input", false);
      }
    },
  },
};
</script>
